import type { PhoneNumber } from 'libphonenumber-js'
import parsePhoneNumber, {
  getCountryCallingCode,
  type CarrierCode,
  type CountryCallingCode,
  type CountryCode,
  type E164Number,
  type NationalNumber,
  type NumberType,
} from 'libphonenumber-js/mobile'
import * as z from 'zod/v4'

export type PhoneData = {
  phoneNumber?: E164Number
  countryCode?: CountryCode
  countryCallingCode?: CountryCallingCode
  carrierCode?: CarrierCode
  nationalNumber?: NationalNumber
  internationalNumber?: string
  phoneNumberValue?: string
  possibleCountries?: string
  isValid?: boolean
  isPossible?: boolean
  uri?: string
  type?: NumberType
}

export const phoneNumberSchema = z.custom<string>(
  (data) => typeof data === 'string' && !!getPhoneData(data)?.isPossible
)

export function getPhoneData(phone: string, defaultCountry?: CountryCode) {
  const countryCallingCode = getCountryCallingCode(defaultCountry ?? 'FI')
  const intlPhone = phone.startsWith('+')
    ? phone
    : phone.startsWith(countryCallingCode)
      ? `+${phone}`
      : `+${countryCallingCode}${phone}`

  const number = parsePhoneNumber(intlPhone, {
    defaultCountry,
    extract: false,
  })

  if (!number) {
    return undefined
  }

  return createPhoneData(number)
}

export function createPhoneData(number: PhoneNumber) {
  const phoneNumber = number?.number
  const intlPhone = !phoneNumber || phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`

  return {
    phoneNumber: intlPhone,
    E164Number: number?.number,
    nationalNumber: number?.nationalNumber,
    internationalNumber: number?.formatInternational(),
    countryCode: number?.country as CountryCode | undefined,
    isValid: number?.isValid(),
    isPossible: number?.isPossible(),
    type: number?.getType(),
  }
}

export function getDisplayPhoneNumber(phone: string) {
  return getPhoneData(phone)?.internationalNumber ?? phone
}
